import React, { useMemo, useRef, useState } from 'react';
import { Form, Input, Tabs, Space, Button } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { isDefaultLocale, useActiveLocales } from '../../hooks/useActiveLocales';

type TranslationMap = Record<string, Record<string, unknown>>;

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).map((s) => s.trim()).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value.split('\n').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function mergePreservedLocales(
  cleaned: TranslationMap,
  formTranslations: TranslationMap | undefined,
  existing?: TranslationMap | null,
): TranslationMap {
  const submittedLocales = new Set(Object.keys(formTranslations || {}));
  const merged: TranslationMap = { ...cleaned };

  if (existing) {
    for (const [locale, data] of Object.entries(existing)) {
      if (isDefaultLocale(locale)) continue;
      if (submittedLocales.has(locale)) continue;
      if (data && typeof data === 'object') {
        merged[locale] = data;
      }
    }
  }

  return merged;
}

/** Strip empty locales from exercise translations payload. Merge with existing. */
export function normalizeExerciseTranslations(
  translations?: TranslationMap,
  existing?: TranslationMap | null,
): TranslationMap | null {
  const cleaned: TranslationMap = {};

  if (translations && typeof translations === 'object') {
    for (const [locale, data] of Object.entries(translations)) {
      if (!data || typeof data !== 'object') continue;
      if (isDefaultLocale(locale)) continue;

      const name = typeof data.name === 'string' ? data.name.trim() : '';
      const description = typeof data.description === 'string' ? data.description.trim() : '';
      const instructions = normalizeStringArray(data.instructions);
      const tips = normalizeStringArray(data.tips);

      if (!name && !description && instructions.length === 0 && tips.length === 0) continue;

      cleaned[locale] = {
        ...(name ? { name } : {}),
        ...(description ? { description } : {}),
        ...(instructions.length > 0 ? { instructions } : {}),
        ...(tips.length > 0 ? { tips } : {}),
      };
    }
  }

  const merged = mergePreservedLocales(cleaned, translations, existing);
  return Object.keys(merged).length > 0 ? merged : null;
}

function tabLabel(label: string, hasError: boolean): React.ReactNode {
  if (!hasError) return label;
  return (
    <span>
      {label}
      <span style={{ color: '#ff4d4f', marginLeft: 4 }}>•</span>
    </span>
  );
}

const StringListField: React.FC<{
  name: string | (string | number)[];
  label: string;
  placeholder: string;
  addLabel: string;
}> = ({ name, label, placeholder, addLabel }) => (
  <Form.Item label={label}>
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name: fieldName, ...restField }) => (
            <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
              <Form.Item {...restField} name={fieldName} style={{ marginBottom: 0 }}>
                <Input placeholder={placeholder} style={{ width: 400 }} />
              </Form.Item>
              <MinusCircleOutlined onClick={() => remove(fieldName)} />
            </Space>
          ))}
          <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} style={{ width: 400 }}>
            {addLabel}
          </Button>
        </>
      )}
    </Form.List>
  </Form.Item>
);

/**
 * Language tabs for exercise localized fields.
 * Default locale (en) writes top-level fields; other locales write into translations[locale].
 */
export const ExerciseLanguageTabs: React.FC = () => {
  const { locales } = useActiveLocales();
  const form = Form.useFormInstance();
  const [activeKey, setActiveKey] = useState('en');
  const lastErrorSig = useRef('');

  const otherLocales = useMemo(
    () => locales.filter((l) => !isDefaultLocale(l.code)),
    [locales],
  );
  const defaultLabel =
    locales.find((l) => isDefaultLocale(l.code))?.label || 'English';

  return (
    <Form.Item noStyle shouldUpdate>
      {() => {
        const errorFields = form
          .getFieldsError()
          .filter((f) => f.errors.length > 0);
        const errorSig = errorFields.map((e) => e.name.join('.')).join('|');

        if (errorFields.length > 0 && errorSig !== lastErrorSig.current) {
          lastErrorSig.current = errorSig;
          const first = errorFields[0]?.name;
          let target: string | null = null;
          if (Array.isArray(first)) {
            if (
              first[0] === 'name' ||
              first[0] === 'description' ||
              first[0] === 'instructions' ||
              first[0] === 'tips'
            ) {
              target = 'en';
            } else if (first[0] === 'translations' && first[1]) {
              target = String(first[1]);
            }
          }
          if (target && target !== activeKey) {
            queueMicrotask(() => setActiveKey(target!));
          }
        } else if (errorFields.length === 0) {
          lastErrorSig.current = '';
        }

        const enHasError = errorFields.some(
          (e) =>
            Array.isArray(e.name) &&
            (e.name[0] === 'name' ||
              e.name[0] === 'description' ||
              e.name[0] === 'instructions' ||
              e.name[0] === 'tips'),
        );

        const items = [
          {
            key: 'en',
            label: tabLabel(defaultLabel, enHasError),
            forceRender: true,
            children: (
              <div style={{ paddingTop: 8 }}>
                <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                  <Input placeholder="e.g. Barbell Bench Press" />
                </Form.Item>
                <Form.Item label="Description" name="description">
                  <Input.TextArea rows={4} placeholder="Describe the exercise..." />
                </Form.Item>
                <StringListField
                  name="instructions"
                  label="Instructions"
                  placeholder="Step description"
                  addLabel="Add instruction"
                />
                <StringListField
                  name="tips"
                  label="Tips"
                  placeholder="Tip"
                  addLabel="Add tip"
                />
              </div>
            ),
          },
          ...otherLocales.map((locale) => {
            const localeHasError = errorFields.some(
              (e) =>
                Array.isArray(e.name) &&
                e.name[0] === 'translations' &&
                e.name[1] === locale.code,
            );
            return {
              key: locale.code,
              label: tabLabel(locale.label, localeHasError),
              forceRender: true,
              children: (
                <div style={{ paddingTop: 8 }}>
                  <Form.Item label="Name" name={['translations', locale.code, 'name']}>
                    <Input placeholder="Translated name" />
                  </Form.Item>
                  <Form.Item label="Description" name={['translations', locale.code, 'description']}>
                    <Input.TextArea rows={4} placeholder="Translated description" />
                  </Form.Item>
                  <StringListField
                    name={['translations', locale.code, 'instructions']}
                    label="Instructions"
                    placeholder="Step description"
                    addLabel="Add instruction"
                  />
                  <StringListField
                    name={['translations', locale.code, 'tips']}
                    label="Tips"
                    placeholder="Tip"
                    addLabel="Add tip"
                  />
                </div>
              ),
            };
          }),
        ];

        return (
          <Tabs
            activeKey={activeKey}
            onChange={setActiveKey}
            items={items}
            style={{ marginBottom: 8 }}
          />
        );
      }}
    </Form.Item>
  );
};
